/**
 * Confirm-it's-you before a sensitive action: pick one of the caller's own
 * methods, prove it, and run the action with the proof. A rejected action
 * (wrong code) stays in the flow so the user can retry.
 */

import type { ChallengeResponse, LoginMfaMethod, MfaMethod, StepUpProof } from '@dealr/core-account/domain';
import { accountErrorCode, accountErrorMessage, toAccountApiError } from '@dealr/api-account';
import {
  RECOVERY_REQUEST_STARTED,
  SESSION_APPROVAL_METHOD_UUID,
  SESSION_APPROVAL_TIMEOUT_MS,
  STEP_UP_APPROVAL_DENIED,
  approvalMatchNumber,
  backupCodeMethod,
  matchNumberPrompt,
  isBackupCodeMethod,
  recoveryOptions,
  sessionApprovalPollDelay,
  type RecoveryOptionType,
  type RecoveryOptionView,
  isValidMethodCode,
  methodDisplayName,
  methodSecondary,
  methodTypeLabel,
  recoveryErrorMessage,
  stepUpKind,
  stepUpPrompt,
} from '@dealr/core-account';
import type { WebAuthnPort } from './ports';
import { SecondFactorError, codeProof, startSecondFactor } from './secondFactor';
import { createStateBox, type Observable } from './store';

type StepUpMethod = MfaMethod | LoginMfaMethod;

export interface StepUpState {
  methods: readonly StepUpMethod[];
  method: StepUpMethod | null;
  needsCode: boolean;
  /** The code was just texted/called. */
  sent: boolean;
  code: string;
  busy: boolean;
  error: string;
  codeError: string;
  /** The action succeeded; the adapter closes its dialog. */
  done: boolean;
  isBackup: boolean;
  codeLooksValid: boolean;
  /** What to show above the code input (or while waiting for a key). */
  prompt: string;
  canSwitch: boolean;
  /** "Lost your phone?": what else works here (backup code, another device, a recovery request). */
  recovery: RecoveryOptionView[];
  /** Waiting for another signed-in device to approve. */
  waiting: boolean;
  /** While waiting: the number the other device must pick, shown large. */
  matchNumber: number | null;
  /** A recovery request started: the dialog shows this and a Close button. */
  requestStarted: string;
}

export interface StepUpDeps {
  methods: readonly StepUpMethod[];
  /** `action` is passed only for another-device approval, so the approver reads what the change is. */
  challenge(methodUuid: string, action?: string): PromiseLike<ChallengeResponse>;
  /** The change being confirmed (`change_password`, `remove_method`, …), named in the other device's prompt. */
  approvalAction?: string;
  webauthn: WebAuthnPort | null;
  action(proof: StepUpProof): PromiseLike<unknown>;
  /** `recovery_options` known up front (a failed step-up's 403 replaces them). */
  recoveryOptions?: unknown;
  /** Another-device approval: `POST /step-up/status`. Without it, that option is hidden. */
  stepUpStatus?(approvalToken: string): PromiseLike<{ status: string }>;
  /** `POST /account-security/recovery-request`. Without it, that option is hidden. */
  recoveryRequest?(): PromiseLike<unknown>;
  /** The poll's timer, from the host (tests run it instantly). Without it, another-device approval is hidden. */
  delay?(ms: number): PromiseLike<unknown>;
}

export interface StepUpFlow extends Observable<StepUpState> {
  /** Picks a method up front when the choice is obvious (a lone method that sends nothing by itself). */
  autoStart(): void;
  choose(method: StepUpMethod): Promise<void>;
  setCode(code: string): void;
  submit(): Promise<void>;
  reset(): void;
  methodName(method: StepUpMethod): string;
  methodType(method: StepUpMethod): string;
  /** The masked phone and the user's nickname ("Named: …"), or ''. */
  methodSecondary(method: StepUpMethod): string;
  recover(type: RecoveryOptionType): Promise<void>;
  /** Stops waiting for another device. */
  cancelWait(): void;
}

/** What a step-up can actually run, given the ports it was handed. */
function stepUpOptions(raw: unknown, deps: StepUpDeps): RecoveryOptionView[] {
  return recoveryOptions(raw, 'step-up').filter((o) =>
    o.type === 'session_approval' ? !!deps.stepUpStatus && !!deps.delay : o.type === 'recovery_request' ? !!deps.recoveryRequest : true,
  );
}

/** A rejection as the user should read it: a tiered error code's own message first. */
export function flowErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof SecondFactorError) return error.message;
  return recoveryErrorMessage(accountErrorCode(error)) ?? accountErrorMessage(error, fallback);
}

export function createStepUpFlow(deps: StepUpDeps): StepUpFlow {
  const box = createStateBox<StepUpState>({
    methods: deps.methods,
    method: null,
    needsCode: false,
    sent: false,
    code: '',
    busy: false,
    error: '',
    codeError: '',
    done: false,
    isBackup: false,
    codeLooksValid: false,
    prompt: '',
    canSwitch: deps.methods.length > 1,
    recovery: stepUpOptions(deps.recoveryOptions, deps),
    waiting: false,
    matchNumber: null,
    requestStarted: '',
  });
  let waitRun = 0;
  /** A failed step-up (403 `step_up_failed`) brings the server's recovery options with it. */
  function absorbOptions(error: unknown): void {
    if (accountErrorCode(error) !== 'step_up_failed') return;
    const data = toAccountApiError(error).data as Record<string, unknown> | null;
    const options = stepUpOptions(data && data['recovery_options'], deps);
    if (options.length) box.set({ recovery: options });
  }

  function derive(patch: Partial<StepUpState>): Partial<StepUpState> {
    const next = { ...box.state, ...patch };
    const m = next.method;
    return {
      ...patch,
      isBackup: isBackupCodeMethod(m),
      codeLooksValid: !!m && isValidMethodCode(m.type, next.code),
      prompt: !m ? '' : next.busy && !next.needsCode ? `Waiting for ${methodDisplayName(m)}…` : stepUpPrompt(m),
    };
  }
  const set = (patch: Partial<StepUpState>) => box.set(derive(patch));

  async function run_(approvalToken: string): Promise<void> {
    await run({ method_uuid: SESSION_APPROVAL_METHOD_UUID, approval_token: approvalToken });
  }

  async function run(proof: StepUpProof): Promise<void> {
    set({ busy: true });
    await deps.action(proof);
    set({ busy: false, done: true });
  }

  const flow: StepUpFlow = {
    get state() {
      return box.state;
    },
    subscribe: box.subscribe,
    autoStart() {
      const real = deps.methods.filter((m) => !isBackupCodeMethod(m));
      const lone = real.length === 1 ? real[0]! : null;
      if (deps.methods.length === 1 || (lone && stepUpKind(lone.type) !== 'send-code')) {
        void flow.choose(lone ?? deps.methods[0]!);
      }
    },
    async choose(method) {
      set({ method, needsCode: false, code: '', error: '', codeError: '', busy: true });
      try {
        const step = await startSecondFactor(method, {
          challenge: (uuid) => deps.challenge(uuid),
          webauthn: deps.webauthn,
        });
        if (step.needsCode) {
          set({ needsCode: true, sent: step.sent, busy: false });
          return;
        }
        await run(step.proof);
      } catch (error) {
        absorbOptions(error);
        set({ busy: false, error: flowErrorMessage(error, 'Verification could not start. Try another method.') });
      }
    },
    setCode(code) {
      set({ code, codeError: '' });
    },
    async submit() {
      const { method, busy, codeLooksValid, code } = box.state;
      if (!method || busy) return;
      if (!codeLooksValid) {
        set({ codeError: 'Enter the code exactly as shown.' });
        return;
      }
      set({ codeError: '', error: '', busy: true });
      try {
        await run(codeProof(method, code));
      } catch (error) {
        absorbOptions(error);
        set({ busy: false, code: '', error: flowErrorMessage(error, 'That code did not work. Try again.') });
      }
    },
    reset() {
      waitRun++;
      set({ method: null, needsCode: false, code: '', error: '', codeError: '', waiting: false, matchNumber: null, busy: false });
    },
    cancelWait() {
      waitRun++;
      if (box.state.waiting) set({ waiting: false, matchNumber: null, busy: false, method: null });
    },
    async recover(type) {
      if (box.state.busy) return;
      if (type === 'backup_code') {
        const backup = deps.methods.find((m) => isBackupCodeMethod(m)) ?? (backupCodeMethod() as StepUpMethod);
        set({ method: backup, needsCode: true, sent: false, code: '', error: '', codeError: '', busy: false });
        return;
      }
      if (type === 'recovery_request' && deps.recoveryRequest) {
        set({ busy: true, error: '' });
        try {
          await deps.recoveryRequest();
          set({ busy: false, requestStarted: RECOVERY_REQUEST_STARTED });
        } catch (error) {
          set({ busy: false, error: flowErrorMessage(error, 'The request couldn’t be started. Try again in a moment.') });
        }
        return;
      }
      if (type === 'session_approval' && deps.stepUpStatus && deps.delay) {
        const run = ++waitRun;
        box.set({ busy: true, waiting: true, matchNumber: null, error: '', method: null, needsCode: false, prompt: 'Approve this on a device where you’re signed in. We’ll continue as soon as you do.' });
        try {
          const reply = await deps.challenge(SESSION_APPROVAL_METHOD_UUID, deps.approvalAction);
          const approvalToken = (reply as { approval_token?: string }).approval_token;
          if (!approvalToken) throw new SecondFactorError('Another device couldn’t be asked. Try another option.');
          const matchNumber = approvalMatchNumber(reply);
          if (run !== waitRun) return;
          if (matchNumber !== null) box.set({ matchNumber, prompt: `${matchNumberPrompt(matchNumber, 'step_up')}.` });
          const started = Date.now();
          for (let attempt = 0; run === waitRun; attempt++) {
            if (Date.now() - started >= SESSION_APPROVAL_TIMEOUT_MS) throw new SecondFactorError('No one approved this in time. Try again or choose another option.');
            await deps.delay!(sessionApprovalPollDelay(attempt));
            if (run !== waitRun) return;
            const { status } = await deps.stepUpStatus(approvalToken);
            if (status === 'approved') break;
            if (status === 'denied') throw new SecondFactorError(`${STEP_UP_APPROVAL_DENIED} Choose another option.`);
            if (status === 'none') throw new SecondFactorError('The other device didn’t approve this. Choose another option.');
          }
          if (run !== waitRun) return;
          box.set({ waiting: false, matchNumber: null });
          await run_(approvalToken);
        } catch (error) {
          if (run !== waitRun) return;
          absorbOptions(error);
          box.set({ busy: false, waiting: false, matchNumber: null, prompt: '', error: flowErrorMessage(error, 'Another device couldn’t approve this. Try another option.') });
        }
      }
    },
    methodName: (method) => methodDisplayName(method),
    methodType: (method) => methodTypeLabel(method.type),
    methodSecondary: (method) => methodSecondary(method),
  };
  return flow;
}
